import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import { ControlLabel, FormGroup, OverlayTrigger, Popover } from 'react-bootstrap';
import Select, { components } from 'react-select';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCircle } from '@fortawesome/pro-solid-svg-icons';

import { isPresent } from 'helpers/string_helpers';
import IconFormControl from 'components/icon_form_control';
import UserSelect from 'components/users/user_select';

import TagsFilter from './tags_filter';

import ManagedInput from 'components/form/managed_input';
import { TASK_STATUSES, TASK_TYPES } from 'helpers/tasks/constants';
import { delayRetrieveTasks, retrieveTags, retrieveTasks } from './store/actions/search';
import {
  changeAssigneeFilter,
  changeCreatorFilter,
  changeEndDateFilter,
  changeSearchText,
  changeStartDateFilter,
  changeStatusFilter,
  changeTaskTypeFilter,
  changeTagsFilter,
  toggleIncludeUntagged
} from './store/actions/query';

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <FontAwesomeIcon className="caret-down ml-1" icon={faCaretDown} />
    </components.DropdownIndicator>
  );
};

export class Filters extends React.Component {
  static propTypes = {
    additionalActions: PropTypes.array,
    assigneeId: PropTypes.number,
    assigneeLabel: PropTypes.string,
    availableTags: PropTypes.array,
    creatorId: PropTypes.number,
    creatorLabel: PropTypes.string,
    currentUser: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
    delayRetrieveTasks: PropTypes.func.isRequired,
    endDate: PropTypes.string,
    hideLabels: PropTypes.bool,
    hideSearchTextFilter: PropTypes.bool,
    hideStatus: PropTypes.bool,
    hideTaskTypeFilter: PropTypes.bool,
    includeUntagged: PropTypes.bool,
    mobileView: PropTypes.bool,
    retrieveTags: PropTypes.func.isRequired,
    retrieveTasks: PropTypes.func.isRequired,
    searchFiltersStyleOverrides: PropTypes.object,
    searchText: PropTypes.string,
    startDate: PropTypes.string,
    status: PropTypes.string,
    tagIds: PropTypes.array,
    type: PropTypes.string,
    users: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      imageUrl: PropTypes.string.isRequired
    })).isRequired,
    onChangeAssignee: PropTypes.func.isRequired,
    onChangeCreator: PropTypes.func.isRequired,
    onChangeEndDate: PropTypes.func.isRequired,
    onChangeSearchText: PropTypes.func.isRequired,
    onChangeStartDate: PropTypes.func.isRequired,
    onChangeStatus: PropTypes.func.isRequired,
    onChangeTags: PropTypes.func.isRequired,
    onChangeTaskType: PropTypes.func.isRequired,
    onToggleIncludeUntagged: PropTypes.func.isRequired
  };

  handleSelect = (callback, valueSelector) => (value) => {
    valueSelector = valueSelector || (selectedValue => selectedValue);

    callback(valueSelector(value));
    this.props.retrieveTasks();
  };

  handleAssigneeSelect = this.handleSelect(this.props.onChangeAssignee);
  handleCreatorSelect = this.handleSelect(this.props.onChangeCreator);
  handleStatusSelect = this.handleSelect(
    this.props.onChangeStatus,
    selectedValue => selectedValue.value
  );
  handleTaskTypeSelect = this.handleSelect(
    this.props.onChangeTaskType,
    selectedValue => selectedValue.value
  );

  handleChangeSearchText = (e) => {
    const { delayRetrieveTasks, onChangeSearchText } = this.props;

    onChangeSearchText(e.target.value);
    delayRetrieveTasks();
  };

  handleChangeEndDate = (date) => {
    const { onChangeEndDate, retrieveTasks } = this.props;

    onChangeEndDate(date);
    retrieveTasks();
  };

  handleChangeStartDate = (date) => {
    const { onChangeStartDate, retrieveTasks } = this.props;

    onChangeStartDate(date);
    retrieveTasks();
  };

  handleTagChange = (tags) => {
    this.props.onChangeTags(tags);
    this.props.retrieveTasks();
  };

  handleToggleIncludeUntagged = () => {
    this.props.onToggleIncludeUntagged();
    this.props.retrieveTasks();
  };

  render() {
    const {
      additionalActions,
      hideStatus,
      searchFiltersStyleOverrides,
      hideSearchTextFilter,
      hideTaskTypeFilter
    } = this.props;

    return (
      <div
        className="search-filters"
        style={searchFiltersStyleOverrides || {}}
      >
        {hideSearchTextFilter || this.renderSearchTextFilter()}
        {this.renderTagsFilter()}
        {this.renderAssigneeFilter()}
        {this.renderCreatorFilter()}
        {this.renderDateFilter()}
        {hideStatus || this.renderStatusFilter()}
        {hideTaskTypeFilter || this.renderTaskTypeFilter()}
        <div className="additional-filters">
          {additionalActions}
        </div>
      </div>
    );
  }

  renderAssigneeFilter() {
    const { assigneeId, users, hideLabels, assigneeLabel } = this.props;
    const assigneeFilterProps = { flex: '2 0 150px', maxWidth: '410px' };

    if (!this.props.hideSearchTextFilter) {
      assigneeFilterProps.marginLeft = '10px';
    }

    return (
      <div className="filter" style={assigneeFilterProps}>
        <FormGroup>
          {hideLabels || <ControlLabel>Assignee</ControlLabel>}
          <UserSelect
            blankOption
            classNamePrefix="task-filter-select"
            components={{ DropdownIndicator }}
            displayImage={false}
            fixedLabel={assigneeLabel}
            name="assignee-filter"
            placeholder="Assignee: All"
            users={users}
            value={assigneeId}
            onChange={this.handleAssigneeSelect}
          />
        </FormGroup>
      </div>
    );
  }

  renderCreatorFilter() {
    const { creatorId, users, hideLabels, creatorLabel } = this.props;

    return (
      <div
        className="filter"
        style={{ flex: '2 2 150px', minWidth: 150, maxWidth: 410 }}
      >
        <FormGroup>
          {hideLabels || <ControlLabel>Creator</ControlLabel>}
          <UserSelect
            blankOption
            classNamePrefix="task-filter-select"
            components={{ DropdownIndicator }}
            displayImage={false}
            fixedLabel={creatorLabel}
            name="creator-filter"
            placeholder="Creator: All"
            users={users}
            value={creatorId}
            onChange={this.handleCreatorSelect}
          />
        </FormGroup>
      </div>
    );
  }

  renderDateFilter() {
    let { endDate, startDate } = this.props;
    const { hideLabels } = this.props;

    startDate = isPresent(startDate) ? moment(startDate) : null;
    endDate = isPresent(endDate) ? moment(endDate) : null;

    return (
      <div className="filter filter-datepicker" style={{ flex: '0 1 auto' }}>
        <FormGroup>
          {hideLabels || <ControlLabel>Date</ControlLabel>}
          <div className="d-flex align-items-center">
            <ManagedInput
              className="form-control"
              endDate={endDate}
              isClearable
              name="date-filter-min"
              placeholderText="Start Date"
              selectsStart
              startDate={startDate}
              type="date"
              value={startDate}
              onValueChange={this.handleChangeStartDate}
            />
            &nbsp;
            <ManagedInput
              className="form-control ml-2"
              endDate={endDate}
              isClearable
              minDate={startDate}
              name="date-filter-max"
              placeholderText="End Date"
              selectsEnd
              startDate={startDate}
              type="date"
              value={endDate}
              onValueChange={this.handleChangeEndDate}
            />
          </div>
        </FormGroup>
      </div>
    );
  }

  renderSearchTextFilter() {
    const { searchText } = this.props;

    return (
      <div className="filter" style={{ flex: '1 0 auto' }}>
        <IconFormControl
          name="search-text-filter"
          placeholder="Search your tasks"
          value={searchText}
          onChange={this.handleChangeSearchText}
        />
      </div>
    );
  }

  renderStatusFilter() {
    const { status, hideLabels, mobileView } = this.props;
    let options = Object.values(TASK_STATUSES).concat({ label: 'All', value: 'all' });

    if (mobileView) {
      options = options.concat([
        { label: 'Today', value: 'today' },
        { label: 'Tomorrow', value: 'tomorrow' },
        { label: 'This Week', value: 'this_week' },
      ]);
    }

    const selectedOption = options.find(option => option.value === status);

    return (
      <div className="filter" style={{ flex: '0 0 200px' }}>
        <FormGroup>
          {hideLabels || <ControlLabel>Status</ControlLabel>}
          <Select
            classNamePrefix="task-filter-select"
            components={{ DropdownIndicator }}
            isSearchable={false}
            name="status-filter"
            options={options}
            placeholder="Status"
            value={selectedOption}
            onChange={this.handleStatusSelect}
          />
        </FormGroup>
      </div>
    );
  }

  renderTaskTypeFilter() {
    const { type, hideLabels } = this.props;
    const options = [{ label: 'All', value: null }].concat(Object.values(TASK_TYPES));
    const selectedOption = options.find(option => option.value === type);

    return (
      <div className="filter" style={{ flex: '0 0 155px' }}>
        <FormGroup>
          {hideLabels || <ControlLabel>Type</ControlLabel>}
          <Select
            classNamePrefix="task-filter-select"
            components={{ DropdownIndicator }}
            isSearchable={false}
            name="task-type-filter"
            options={options}
            value={selectedOption}
            onChange={this.handleTaskTypeSelect}
          />
        </FormGroup>
      </div>
    );
  }

  renderTagsFilter() {
    const { availableTags, includeUntagged, retrieveTags, tagIds } = this.props;

    const tooltip = (
      <Popover id="tags">
        <div className="top-buffer-sm row right-buffer-sm left-buffer-sm">
          <TagsFilter
            includeUntagged={includeUntagged}
            selectedIds={tagIds}
            tags={availableTags}
            onSelectedChange={this.handleTagChange}
            onToggleIncludeUntagged={this.handleToggleIncludeUntagged}
          />
        </div>
      </Popover>
    );

    return (
      <div className="filter" style={{ flex: '0 0 120px' }}>
        <OverlayTrigger
          name="tags-filter"
          overlay={tooltip}
          placement="bottom"
          rootClose
          trigger="click"
          onEnter={() => retrieveTags()}
        >
          <div className="btn btn-light d-flex justify-content-between w-100">
            <div className="d-flex">
              Tags
              {
                tagIds.length > 0 && (
                  <span className="pl-3">
                    <FontAwesomeIcon icon={faCircle} size="xs" />
                  </span>
                )
              }
            </div>
            <div>
              <span className="caret" />
            </div>
          </div>
        </OverlayTrigger>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  assigneeId: state.query.assigneeId,
  availableTags: state.search.tags,
  creatorId: state.query.creatorId,
  currentUser: state.static.currentUser,
  endDate: state.query.endDate,
  includeUntagged: state.query.includeUntagged,
  searchText: state.query.searchText,
  startDate: state.query.startDate,
  status: state.query.status,
  tagIds: state.query.tagIds,
  type: state.query.taskType,
  users: state.static.databaseMembers
});

const mapDispatchToProps = {
  delayRetrieveTasks,
  retrieveTags,
  retrieveTasks,
  onChangeAssignee: changeAssigneeFilter,
  onChangeCreator: changeCreatorFilter,
  onChangeEndDate: changeEndDateFilter,
  onChangeSearchText: changeSearchText,
  onChangeStartDate: changeStartDateFilter,
  onChangeStatus: changeStatusFilter,
  onChangeTaskType: changeTaskTypeFilter,
  onChangeTags: changeTagsFilter,
  onToggleIncludeUntagged: toggleIncludeUntagged
};

export default connect(mapStateToProps, mapDispatchToProps)(Filters);
